/**
 * Email Connections Service for Outlook Add-in
 * Handles fetching user's email connections from DoNexus API
 */

import apiClient from '../utils/apiClient';

class EmailConnectionsService {
  constructor() {
    this.cachedConnections = null;
    this.pendingRequest = null;
  }

  /**
   * Get all email connections for the current user
   * @param {boolean} forceRefresh - Force a fresh fetch even if cached data exists
   * @returns {Promise<Array>} Array of email connection objects
   */
  async getAll(forceRefresh = false) {
    try {
      // Return cached data if available and not forcing refresh
      if (!forceRefresh && this.cachedConnections) {
        console.log('Returning cached email connections');
        return this.cachedConnections;
      }

      // If there's already a pending request, return it
      if (this.pendingRequest) {
        console.log('Reusing pending email connections request');
        return await this.pendingRequest;
      }

      // Create new request
      console.log('Fetching fresh email connections');
      this.pendingRequest = (async () => {
        try {
          const response = await apiClient.get('/email-connections');
          
          if (response.success && response.data) {
            // Cache the connections
            this.cachedConnections = response.data;
            return response.data;
          } else {
            throw new Error(response.error || 'Failed to fetch email connections');
          }
        } finally {
          // Clear pending request after completion
          this.pendingRequest = null;
        }
      })();

      return await this.pendingRequest;
    } catch (error) {
      console.error('Error fetching email connections:', error);
      this.pendingRequest = null;
      throw error;
    }
  }

  /**
   * Get all connection IDs for the current user
   * @param {boolean} forceRefresh - Force a fresh fetch
   * @returns {Promise<Array<string>>} Array of connection IDs
   */
  async getConnectionIds(forceRefresh = false) {
    try {
      const connections = await this.getAll(forceRefresh);
      return connections.map(conn => conn.id);
    } catch (error) {
      console.error('Error getting connection IDs:', error);
      return [];
    }
  }

  /**
   * Clear cached connections data
   */
  clearCache() {
    console.log('Clearing email connections cache');
    this.cachedConnections = null;
    this.pendingRequest = null;
  }
}

// Create singleton instance
const emailConnectionsService = new EmailConnectionsService();

export default emailConnectionsService;

